import React, { useEffect, useState, useRef, useContext } from "react";
import { AiOutlineSearch, AiOutlineCloseCircle } from "react-icons/ai";
import Utils from "../../../utils/Utils";
import Link from "next/link";
import { TableHeader } from "../../../components/table/TableHeader";
import TableCellPercentile from "../../../components/table/TableCellPercentile";
import Input from "../../../components/form/Input";
import { AppContext } from "../../../context/state";
import TableSkeleton from "../../../components/table/TableSkeleton";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { BsChevronRight, BsChevronLeft } from "react-icons/bs";
import { AiOutlineDownload } from "react-icons/ai";
import IconButton from "../../../components/button/IconButton";
import Head from "next/head";
import MainLayout from "../../../components/MainLayout/MainLayout";
import { fetchLeaguePlayerData } from "../../../utils/getLeaguePlayerStats";
import { useRouter } from "next/router";
import Loader from "react-loader-spinner";
import { getYearArray } from "../../../constants/main";
let ascOrder = true;

const leagueInfo = {
  nba: {
    title: "NBA",
    maxYear: "2026",
    apiLeague: "nba",
  },
  wnba: {
    title: "WNBA",
    maxYear: "2025",
    apiLeague: "wnba",
  },
  ncaa: {
    title: "NCAA",
    maxYear: "2026",
    apiLeague: "college_mens",
  },
  ncaaw: {
    title: "NCAAW",
    maxYear: "2024",
    apiLeague: "college_womens",
  },
};
const tableHeader = [
  {
    title: "Players",
    showImage: false,
    sortable: true,
    key: "Players",
  },
  {
    title: "Team",
    showImage: false,
    sortable: true,
    key: "Team",
  },
  {
    title: "Possessions",
    showImage: false,
    sortable: true,
    key: "Possessions",
  },
  {
    title: "SQ PPP",
    showImage: false,
    sortable: true,
    key: "SQ PPP",
    tooltip: {
      link: "/stats-explained#sq-points",
      title: "Shot Quality Points per Possession",
      text: "Total SQ points scored divided by total possession",
    },
  },
  {
    title: "SQ PPP Rank",
    showImage: false,
    sortable: true,
    key: "SQ PPP Rank",
  },
  {
    title: "Rim & 3 Rate",
    showImage: false,
    sortable: true,
    key: "Rim_and_3_rate",
    tooltip: {
      link: "/stats-explained#Rim&3",
      title: "Rim & 3 Rate",
      text: "Percent of possession that resulted in a three pointer or a shot at the rim (the two most valued shots!)",
    },
  },
  {
    title: "Shot Making",
    showImage: false,
    sortable: true,
    key: "shot_making",
  },
  {
    title: "Shot Making Rank",
    showImage: false,
    sortable: true,
    key: "shot_making_pctile",
  },
];

const PlayerStats = (props) => {
  const router = useRouter();
  let leagueFromUrl = router.query?.league;
  const { data } = props;
  const initialRawPerGame = data.rawPerGame.data.filter((dt) => dt["SQ PPP Rank"] !== null && dt["Players"]);
  const league = props.league;
  const yearArray = getYearArray(leagueFromUrl);
  useEffect(() => {
    setStore({ ...store, leagueType: league });
  }, [league]);

  const { store, setStore } = useContext(AppContext);
  const [year, setYear] = useState(leagueInfo[league].maxYear);
  const [rawPerGame, setRawPerGame] = useState(initialRawPerGame);
  const [rawPerPoss, setRawPerPoss] = useState([]);
  const [records, setRecords] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [sortedKey, setSortedKey] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [perGame, setPerGame] = useState(true);
  const [selectValue, setSelectValue] = useState("ge");
  const [filterInput, setFilterInput] = useState(2);
  const [sortPoints, setSortPoints] = useState({ start: 0, end: 100 });
  const [disableNext, setDisableNext] = useState(false);
  const [disableBack, setDisableBack] = useState(true);
  const [showAllPages, setShowAllPages] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const inputSearchRef = useRef(null);
  const isFirstRender = useRef(true);

  const [user, setUser] = useState(null);
  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("user")));
  }, []);

  const isStandardPlan = Utils.hasStandardPlan(user);
  const isPremiumPlan = Utils.hasPremiumPlan(user);
  const isPayed = isStandardPlan || isPremiumPlan;

  const [searchAutocomplete, setSearchAutocomplete] = useState({
    players: [],
    team: [],
    conferences: [],
  });
  const [displaySearchResults, setDisplaySearchResults] = useState(false);
  const [slicedArray, setSlicedArray] = useState([]);
  const [slicedArrayIndex, setSlicedArrayIndex] = useState(0);

  const handleLoadMore = () => {
    setSlicedArrayIndex(slicedArrayIndex + 40);
  };

  useEffect(() => {
    setSlicedArray(filtered.slice(0, slicedArrayIndex + 40));
  }, [filtered, slicedArrayIndex]);

  useEffect(() => {
    async function load() {
      if (!isFirstRender.current) {
        setIsLoading(true);
        const data = await fetchLeaguePlayerData(league, year);
        setRawPerGame(data.rawPerGame.data.filter((dt) => dt["SQ PPP Rank"] !== null && dt["Players"]));
        setTimeout(() => {
          setIsLoading(false);
        }, 2000);
      } else {
        isFirstRender.current = false;
      }
    }
    load();
  }, [year, league]);

  useEffect(() => {
    if (rawPerGame?.length > 0) {
      setRecords([...rawPerGame]);
      mapData(rawPerGame, true);
      filterData();
      setIsLoading(false);
    }
  }, [rawPerGame, rawPerPoss, league]);

  useEffect(() => {
    if (records.length) {
      let endPoint = records.length > 100 ? 100 : records.length;
      setSortPoints({ start: 1, end: endPoint });
    } else {
      setSortPoints({ start: 0, end: 0 });
    }
  }, [records, league]);

  useEffect(() => {
    let start = sortPoints.start;
    let end = sortPoints.end;
    setDisableBack(start <= 1);
    setDisableNext(end >= records.length);
    mapData(records);
  }, [sortPoints.start, sortPoints.end, records, league]);

  const onNextClick = () => {
    if (disableNext) return;
    let start = sortPoints.start;
    let end = sortPoints.end;
    let total = records.length;
    start = end + 1;
    end = end + 100 < total ? end + 100 : total;
    let newRecords = records.slice(start - 1, end);
    setFiltered([...newRecords]);
    setSortPoints({ start, end });
  };

  const onBackClick = () => {
    if (disableBack) return;
    let start = sortPoints.start;
    let end = sortPoints.end;
    end = start - 1;
    start = start - 100 > 0 ? start - 100 : 1;
    let newRecords = records.slice(start - 1, end);
    setFiltered([...newRecords]);
    setSortPoints({ start, end });
  };

  const sort = (key) => {
    let res = [];
    let rawData = records;

    if (sortedKey === key) {
      let order = ascOrder ? "asc" : "desc";

      res = rawData.sort(Utils.dynamicsort(key, order));
      ascOrder = order === "desc";
    } else {
      ascOrder = false;
      setSortedKey(key);
      res = rawData.sort(Utils.dynamicsort(key));
    }
    setRecords(res);
    mapData(res, true);
  };

  const searchByPlayerName = (searchTerm) => {
    let data = perGame ? rawPerGame : rawPerPoss;
    return Array.from(
      new Set(
        data
          .filter((team) => team["Players"]?.toLowerCase().includes(searchTerm?.toLowerCase()))
          .map((team) => team["Players"])
      )
    );
  };

  const searchByTeam = (searchTerm) => {
    let data = perGame ? rawPerGame : rawPerPoss;
    return Array.from(
      new Set(
        data
          .filter((team) => team["Team"]?.toLowerCase().includes(searchTerm?.toLowerCase()))
          .map((team) => team["Team"])
      )
    );
  };

  const searchByConference = (searchTerm) => {
    let data = perGame ? rawPerGame : rawPerPoss;
    return Array.from(
      new Set(
        data
          .filter((team) => team["Conference"]?.toLowerCase().includes(searchTerm?.toLowerCase()))
          .map((team) => team["Conference"])
      )
    );
  };

  const searchData = (searchTerm, searchType) => {
    let data = perGame ? rawPerGame : rawPerPoss;
    let newRecords;

    if (searchType === "players") {
      newRecords = data.filter((r) => r.Players === searchTerm);
    } else if (searchType === "Conference") {
      newRecords = data.filter((r) => r.Conference === searchTerm);
    } else {
      newRecords = data.filter((r) => r.Team === searchTerm);
    }

    return newRecords.sort(Utils.dynamicsort("SQ PPP", "desc"));
  };

  const filterData = (e) => {
    if (e) {
      e.preventDefault();
    }
    let rec = [...rawPerGame];

    let value = parseInt(filterInput);
    let filteredData = rec.filter((data) => {
      if (selectValue === "ge") {
        return data.Possessions >= value;
      }
      if (selectValue === "le") {
        return data.Possessions <= value;
      }
      if (selectValue === "eq") {
        return data.Possessions === value;
      }
    });

    const recSortedBySQPPP = rec.sort(Utils.dynamicsort("SQ PPP", "desc")).map((dt, i) => {
      return {
        ...dt,
        Rank: i + 1,
      };
    });

    const sortedBySQPPP = filteredData.sort(Utils.dynamicsort("SQ PPP", "desc")).map((dt, i) => {
      return {
        ...dt,
        Rank: i + 1,
      };
    });
    const sessionObj = {};
    const sessionObjRec = {};
    sessionObj[year] = sortedBySQPPP;
    sessionObjRec[year] = recSortedBySQPPP;
    setRecords(sortedBySQPPP);
    mapData(filteredData, true);
  };

  const mapData = (data, reset = false) => {
    let newRecords = data.slice(sortPoints.start - 1, sortPoints.end);
    setFiltered([...newRecords]);
  };

  const onSearchBlur = (e) => {
    setTimeout(() => {
      setDisplaySearchResults(false);
    }, 300);
  };

  const displaySearchData = (e, searchType) => {
    inputSearchRef.current.value = e.target.innerText;
    const newRecords = searchData(e.target.innerText, searchType);
    setRecords(newRecords);
    setDisplaySearchResults(false);
    mapData(newRecords, true);
  };

  const clearSearch = (e) => {
    let data = perGame ? rawPerGame : rawPerPoss;
    setSearchKey("");
    inputSearchRef.current.value = "";
    setDisplaySearchResults(false);
    if (perGame) {
      if (rawPerGame.length) {
        setRecords([...rawPerGame]);
        mapData(rawPerGame, true);
        filterData();
      }
    } else {
      if (rawPerPoss.length) {
        setRecords([...rawPerPoss]);
        mapData(rawPerPoss, true);
        filterData();
      }
    }
  };

  useEffect(() => {
    setSearchAutocomplete({
      players: searchByPlayerName(searchKey).splice(0, 5),
      team: searchByTeam(searchKey).splice(0, 5),
      conferences: searchByConference(searchKey).splice(0, 5),
    });
  }, [searchKey, league]);

  const onShowAllPagesClick = () => {
    setShowAllPages(true);
    setFiltered([...records]);
    setSortPoints({ start: 1, end: records.length });
  };

  const onShowLessPagesClick = () => {
    setShowAllPages(false);
    setSortPoints({ start: 1, end: 100 });
  };

  const handleCsvExport = () => {
    Utils.jsonToCsv(records, "playerStats");
  };

  const onYearClick = (yr) => {
    setIsLoading(true);
    if (year !== yr) {
      setFiltered([]);
      setYear(yr);
    }
  };

  const TableRow = ({ record, showMidHeader, index }) => {
    return (
      <>
        <tr>
          <td className="hard_left" data-name={"Players"}>
            <Link target="_blank" href={`/${leagueFromUrl}/player-props/${record.Team}/${record.Players}`}>
              {record.Players}
            </Link>
          </td>
          <td data-name={"Team"}>
            <Link href={`/team/${leagueInfo[league].apiLeague}/${year}/${record.Team}`}>{record.Team}</Link>
          </td>
          <td data-name={"Possessions"}>{record.Possessions}</td>
          <td data-name={"SQ PPP"}>{record["SQ PPP"]}</td>
          <TableCellPercentile value={record["SQ PPP Rank"]} data_name="SQ PPP Rank" />
          <td data-name={"Rim & 3 Rate"}>{Utils.toPercent(record.Rim_and_3_rate)}</td>
          <td data-name={"Shot Making"}>{`${record["shot_making"] > 0 ? "+" : ""}${record["shot_making"]?.toFixed(
            2
          )}`}</td>
          <TableCellPercentile value={record["shot_making_pctile"]} data_name="Shot Making Rank" />
        </tr>
      </>
    );
  };

  useEffect(() => {
    ascOrder = true;
  }, [league]);

  const showLoadMoreButton = slicedArray.length < filtered.length;

  return (
    <>
      <Head>
        <title>{`${leagueInfo[league].title} Basketball Player Statistics`}</title>
        <meta
          name="description"
          content={`See ${leagueInfo[league].title} Basketball comprehensive Player Statistics Index. Discover in-depth insights for every ${leagueInfo[league].title} player.`}
        />
      </Head>
      <MainLayout>
        <main id="PlayerStats" style={{ minHeight: "100vh" }}>
          <div className="PlayerStats">
            {/* Start Title side */}
            <div className="Titleside text-center">
              <div className="container-fluid">
                <div className="dukescroe">
                  <div
                    className="row align-items-center"
                    style={{ marginRight: "0px", marginLeft: "0px", marginTop: "1rem" }}
                  >
                    <div className="box col-md-7 col-sm-12 text-sm-center text-md-left">
                      <h2>
                        {year} {leagueInfo[league].title} Player Stats
                      </h2>
                    </div>
                    <div
                      className="pagination col-md-5 col-sm-12 justify-content-end align-items-center flex-column flex-md-row d-flex"
                      style={{ columnGap: "1rem" }}
                    >
                      {showAllPages === false ? (
                        <button className="btn btn-primary p-3" onClick={onShowAllPagesClick}>
                          Show All Pages
                        </button>
                      ) : (
                        <button className="btn btn-primary p-3" onClick={onShowLessPagesClick}>
                          Show Less Pages
                        </button>
                      )}
                      {isPremiumPlan && (
                        <IconButton
                          type="button"
                          className="btn btn-orange p-3"
                          onClick={handleCsvExport}
                          icon={<AiOutlineDownload />}
                        >
                          Export to CSV
                        </IconButton>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* End Title side */}
            {/* Start Score side */}
            <div className="Scoreside text-center">
              <div className="container-fluid">
                <div className="dukescroe">
                  <>
                    <div className="row no-after w-100 no-gutters justify-content-around align-items-center">
                      <div className="box col-md-3 text-md-right">
                        {filtered.length > 0 ? (
                          <div className="search position-relative w-100">
                            <Input
                              icon={<AiOutlineSearch />}
                              placeholder="Search Player/Team/Conferences"
                              className="form-control w-100 search-input"
                              ref={inputSearchRef}
                              onInput={(e) => (setSearchKey(e.target.value), setDisplaySearchResults(true))}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") searchData();
                              }}
                              onBlur={onSearchBlur}
                              rightIcon={searchKey.length > 0 ? <AiOutlineCloseCircle onClick={clearSearch} /> : null}
                            />

                            {displaySearchResults === true ? (
                              <div className="search-results">
                                <h6 className="text-left -2">Players</h6>
                                {searchAutocomplete["players"].length > 0 ? (
                                  searchAutocomplete["players"].map((searchTerm) => (
                                    <span
                                      key={searchTerm}
                                      className="search-results__text"
                                      onClick={(e) => displaySearchData(e, "players")}
                                    >
                                      {searchTerm}
                                    </span>
                                  ))
                                ) : (
                                  <span disabled="true" className="text-center">
                                    No Results Found
                                  </span>
                                )}
                                <h6 className="text-left -2">Teams</h6>
                                {searchAutocomplete["team"].length > 0 ? (
                                  searchAutocomplete["team"].map((searchTerm) => (
                                    <span
                                      key={searchTerm}
                                      className="search-results__text"
                                      onClick={(e) => displaySearchData(e, "team")}
                                    >
                                      {searchTerm}
                                    </span>
                                  ))
                                ) : (
                                  <span disabled="true" className="text-center">
                                    No Results Found
                                  </span>
                                )}
                                <h6 className="text-left -2">Conferences</h6>
                                {searchAutocomplete["conferences"].length > 0 ? (
                                  searchAutocomplete["conferences"].map((searchTerm) => (
                                    <span
                                      key={searchTerm}
                                      className="search-results__text"
                                      onClick={(e) => displaySearchData(e, "Conference")}
                                    >
                                      {searchTerm}
                                    </span>
                                  ))
                                ) : (
                                  <span disabled="true" className="text-center">
                                    No Results Found
                                  </span>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <SkeletonTheme color="#a4abb3" highlightColor="#e3e6e8">
                            <Skeleton height="39px" width="100%" style={{ borderRadius: "30px" }} />
                          </SkeletonTheme>
                        )}
                      </div>
                      <div className="d-flex align-items-center">
                        <div className="paginationLinkGroup d-inline-flex">
                          {yearArray.map((yr) => (
                            <div
                              key={yr}
                              className={`paginationLink ${year === yr ? "active" : ""}`}
                              onClick={() => onYearClick(yr)}
                            >
                              {yr}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="Filterside" style={{ width: "500px", position: "relative" }}>
                        <div className="box hide-on-mobile">
                          <p className="text-bold" style={{ textAlign: "left", position: "absolute", top: 0 }}>
                            Filtering Possessions:
                          </p>
                          <div className="row align-items-center">
                            <div className="form-group col-md-3">
                              <select
                                onChange={(e) => setSelectValue(e.target.value)}
                                className="custom-select"
                                defaultValue={selectValue}
                                style={{ paddingLeft: 0, borderRadius: "12px" }}
                              >
                                <option value="ge">&ge;</option>
                                <option value="le">&le;</option>
                                <option value="eq">=</option>
                              </select>
                            </div>
                            <div className="form-group col-md-4">
                              <input
                                type="number"
                                onChange={(e) => {
                                  setFilterInput(e.target.value);
                                }}
                                defaultValue={1}
                                min={0}
                                className="form-control"
                                style={{ borderRadius: "12px", backgroundColor: "#fff" }}
                              />
                            </div>
                            <div className="form-group col-md-5">
                              <button
                                onClick={filterData}
                                type="submit"
                                className="btn w-100"
                                style={{
                                  backgroundColor: "#0f131b",
                                  color: "#fff",
                                  padding: "10px 24px !important",
                                  borderRadius: "10px",
                                }}
                              >
                                APPLY
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {filtered.length > 0 && (
                      <div className="text-center mb-4 hide-on-mobile">
                        <span className="start ml-2">{sortPoints.start}</span>
                        <span>-</span>
                        <span className="end">{sortPoints.end}</span>
                        <span className="ml-2">of</span>
                        <span className="ml-2" style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                          {records.length}
                        </span>

                        <BsChevronLeft
                          onClick={onBackClick}
                          className={disableBack ? "back btns disabled" : "back btns"}
                        />

                        <BsChevronRight
                          onClick={onNextClick}
                          className={disableNext ? "forward btns disabled ml-2" : "forward btns ml-2"}
                        />
                      </div>
                    )}
                    <div className="box year-filter"></div>
                    <div className="box hide-on-mobile"></div>
                  </>

                  {/* end pagination */}

                  {filtered.length > 0 && (
                    <>
                      <div className="table-responsive responsive-to-card table-space">
                        <table className="table">
                          <thead>
                            <tr className="text-center header-container-table">
                              <TableHeader
                                headers={tableHeader}
                                onSortClick={sort}
                                ascOrder={ascOrder}
                                sortedKey={sortedKey}
                              />
                            </tr>
                          </thead>
                          <tbody>
                            {slicedArray.map &&
                              slicedArray.map((record, i) => {
                                const pos = filtered.length > 60 ? parseInt(filtered.length / 2) : null;
                                const showMidHeader = pos === i;

                                return (
                                  <TableRow
                                    record={record}
                                    key={"datarow" + i + record.SQ_Percentile_rank_VISUAL}
                                    showMidHeader={showMidHeader}
                                    index={i}
                                  />
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-center">
                        {showLoadMoreButton && (
                          <button className="btn btn-primary mt-3" onClick={handleLoadMore}>
                            Load More..
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  {!isLoading &&
                    filtered.length === 0 &&
                    (league === "nba" ? (
                      <>
                        <TableSkeleton
                          tableRowsCount={0}
                          tableHeaderCount={12}
                          tableHeaderHeight="142px"
                          className="table-space"
                        ></TableSkeleton>
                        <div
                          style={{
                            display: "flex",

                            marginTop: "50px",
                          }}
                        >
                          <h3 style={{ margin: "0 auto" }}>
                            {" "}
                            🧠 The ShotQuality {leagueInfo[league].title} Plays will be available in 2-4 weeks
                          </h3>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                          <h3 style={{ margin: "0 auto" }}>
                            {`There are no ${leagueInfo[league].title} Player Stats available for ${year}.`}
                          </h3>
                        </div>
                      </>
                    ))}
                  {isLoading && (
                    <div className="loader mt-5">
                      <Loader type={"Oval"} visible color={"orange"} height={60} width={60} />
                      Loading...
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* End Score side */}
            {/* Start Dummy side */}
            <div className="Dummyside">
              <div className="container"></div>
            </div>
            {/* End Dummy side */}
          </div>
        </main>
      </MainLayout>
    </>
  );
};

export default PlayerStats;

export async function getStaticPaths() {
  const leagues = ["nba", "wnba", "ncaa", "ncaaw"];

  const paths = leagues.map((league) => ({
    params: { league },
  }));

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  const { league } = params;
  const data = await fetchLeaguePlayerData(league, leagueInfo[league].maxYear);

  if (!data) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      data,
      league,
    },
    revalidate: 1,
  };
}
